import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAdminAuth";
import { Shield, Loader2 } from "lucide-react";
import Logo from "../components/Logo";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, token } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) navigate("/admin", { replace: true });
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Enter email and password"); return; }
    setLoading(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="glass-bg flex items-center justify-center px-4"
      data-testid="login-page"
    >
      <div className="w-full max-w-sm glass-animate">
        {/* Logo + branding */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 rounded-[20px] overflow-hidden mb-4" style={{ boxShadow: "0 8px 32px rgba(124,92,255,0.18)" }}>
            <img src="/brand/logo-splash.png" alt="1000AD" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight" data-testid="login-title">
            <Logo size="sm" theme="light" /> Admin
          </h1>
          <p className="text-sm text-[#5B617A] mt-1">Platform Console</p>
        </div>

        <form
          onSubmit={submit}
          className="glass-card p-6"
          data-testid="login-form"
        >
          {error && (
            <div
              data-testid="login-error"
              className="text-red-600 text-sm font-medium mb-3 bg-red-50 px-3 py-2 rounded-xl"
            >
              {error}
            </div>
          )}

          <label htmlFor="login-email" className="block text-[#5B617A] text-xs font-semibold mb-1.5 uppercase tracking-wider">
            Email
          </label>
          <input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            autoComplete="email"
            className="w-full h-11 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-[#0B0F1A] text-sm placeholder:text-[#5B617A]/50 glass-input transition-all"
            placeholder="admin@aibm.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="login-password" className="block text-[#5B617A] text-xs font-semibold mb-1.5 mt-4 uppercase tracking-wider">
            Password
          </label>
          <input
            id="login-password"
            data-testid="login-password-input"
            type="password"
            autoComplete="current-password"
            className="w-full h-11 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-[#0B0F1A] text-sm placeholder:text-[#5B617A]/50 glass-input transition-all"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-xl text-white font-semibold text-sm mt-5 flex items-center justify-center gap-2 btn-primary-glass focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Shield className="w-4 h-4" />Sign in</>}
          </button>
        </form>

        <p className="text-center text-[10px] text-[#5B617A]/60 mt-4">
          Platform Admin access only · <Logo size="sm" theme="light" />
        </p>
      </div>
    </div>
  );
}
